import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { usersApi, OrgChartUser, TenantRole } from '../api/client';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { tokens } from '../theme/tokens';

const ALL_PAGE_KEYS = [
  'overview', 'dashboard', 'strategic-alignment', 'portfolios',
  'requests', 'projects', 'financials', 'capacity', 'okrs',
  'roadmaps', 'raid', 'templates', 'timesheets', 'team', 'settings',
];

const PAGE_LABELS: Record<string, string> = {
  overview: 'Overview',
  dashboard: 'Workspace',
  'strategic-alignment': 'Strategic Alignment',
  portfolios: 'Portfolios',
  requests: 'Requests',
  projects: 'Projects',
  financials: 'Financials',
  capacity: 'Capacity',
  okrs: 'OKRs',
  roadmaps: 'Roadmaps',
  raid: 'RAID',
  templates: 'Templates',
  timesheets: 'Timesheets',
  team: 'Team',
  settings: 'Settings',
};

// ── Roles Tab ──────────────────────────────────────────────────────────

interface RoleEditorProps {
  role: TenantRole;
  onSave: (roleId: string, perms: { pages: Record<string, boolean>; canEdit: Record<string, boolean> }) => Promise<void>;
  onDelete: (roleId: string) => Promise<void>;
  onDuplicate: (role: TenantRole) => void;
  userCount: number;
}

const RoleEditor: React.FC<RoleEditorProps> = ({ role, onSave, onDelete, onDuplicate, userCount }) => {
  const perms = useMemo(() => role.permissions || { pages: {}, canEdit: {} }, [role.permissions]);
  const [pages, setPages] = useState<Record<string, boolean>>({ ...perms.pages });
  const [canEdit, setCanEdit] = useState<Record<string, boolean>>({ ...perms.canEdit });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Reset local state when role changes
  useEffect(() => {
    setPages({ ...(perms.pages || {}) });
    setCanEdit({ ...(perms.canEdit || {}) });
  }, [perms]);

  const dirty = useMemo(() => {
    const origPages = perms.pages || {};
    const origEdit = perms.canEdit || {};
    return ALL_PAGE_KEYS.some(k => !!pages[k] !== !!origPages[k] || !!canEdit[k] !== !!origEdit[k]);
  }, [pages, canEdit, perms]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(role.id, { pages, canEdit });
    } finally {
      setSaving(false);
    }
  };

  const togglePage = (key: string) => {
    const newVal = !pages[key];
    setPages(prev => ({ ...prev, [key]: newVal }));
    if (!newVal) setCanEdit(prev => ({ ...prev, [key]: false }));
  };

  const toggleEdit = (key: string) => {
    setCanEdit(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const enabledCount = ALL_PAGE_KEYS.filter(k => pages[k]).length;

  return (
    <Card variant="outlined" sx={{ borderRadius: `${tokens.radius.lg}px`, borderColor: tokens.color.border }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{role.name}</Typography>
            {role.isDefault && <Chip label="Default" size="small" variant="outlined" color="info" />}
            <Chip label={`${userCount} user${userCount !== 1 ? 's' : ''}`} size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              {enabledCount} of {ALL_PAGE_KEYS.length} pages
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Duplicate role">
              <IconButton size="small" onClick={() => onDuplicate(role)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!role.isDefault && (
              <Tooltip title={userCount > 0 ? 'Remove users from this role first' : 'Delete role'}>
                <span>
                  <IconButton size="small" onClick={() => onDelete(role.id)} disabled={userCount > 0}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Button size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Collapse' : 'Edit Permissions'}
            </Button>
          </Box>
        </Box>

        {expanded && (
          <Box sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 100 }}>Can View</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 100 }}>Can Edit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ALL_PAGE_KEYS.map(key => (
                    <TableRow key={key} hover>
                      <TableCell>{PAGE_LABELS[key] || key}</TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={!!pages[key]}
                          onChange={() => togglePage(key)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={!!canEdit[key]}
                          onChange={() => toggleEdit(key)}
                          disabled={!pages[key]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => {
                setPages(Object.fromEntries(ALL_PAGE_KEYS.map(k => [k, true])));
                setCanEdit(Object.fromEntries(ALL_PAGE_KEYS.map(k => [k, true])));
              }}>
                Select All
              </Button>
              <Button size="small" onClick={() => {
                setPages(Object.fromEntries(ALL_PAGE_KEYS.map(k => [k, false])));
                setCanEdit(Object.fromEntries(ALL_PAGE_KEYS.map(k => [k, false])));
              }}>
                Clear All
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!dirty || saving}
                onClick={handleSave}
                sx={{ borderRadius: `${tokens.radius.md}px` }}
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const RolesTab: React.FC<{
  roles: TenantRole[];
  users: OrgChartUser[];
  onReload: () => void;
}> = ({ roles, users, onReload }) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [duplicatePerms, setDuplicatePerms] = useState<{ pages: Record<string, boolean>; canEdit: Record<string, boolean> } | null>(null);
  const [creating, setCreating] = useState(false);

  const userCountByRole = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const u of users) {
      if (u.tenantRoleId) counts[u.tenantRoleId] = (counts[u.tenantRoleId] || 0) + 1;
    }
    return counts;
  }, [users]);

  const handleCreate = async () => {
    if (!newRoleName.trim()) return;
    setCreating(true);
    try {
      const res = await usersApi.createRole(newRoleName.trim());
      if (duplicatePerms) {
        await usersApi.updateRolePermissions(res.data.id, duplicatePerms);
      }
      setNewRoleName('');
      setDuplicatePerms(null);
      setCreateDialogOpen(false);
      onReload();
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (roleId: string, perms: { pages: Record<string, boolean>; canEdit: Record<string, boolean> }) => {
    await usersApi.updateRolePermissions(roleId, perms);
    onReload();
  };

  const handleDelete = async (roleId: string) => {
    await usersApi.deleteRole(roleId);
    onReload();
  };

  const handleDuplicate = (role: TenantRole) => {
    setNewRoleName(`${role.name} (Copy)`);
    setDuplicatePerms(role.permissions ? { pages: { ...role.permissions.pages }, canEdit: { ...role.permissions.canEdit } } : null);
    setCreateDialogOpen(true);
  };

  // Sort: default roles first, then custom
  const sorted = useMemo(() => {
    return [...roles].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {roles.length} role{roles.length !== 1 ? 's' : ''} configured. Assign roles to users in the Users tab.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => { setNewRoleName(''); setDuplicatePerms(null); setCreateDialogOpen(true); }}
          sx={{ borderRadius: `${tokens.radius.md}px` }}
        >
          Create Role
        </Button>
      </Box>

      <Stack spacing={1.5}>
        {sorted.map(role => (
          <RoleEditor
            key={role.id}
            role={role}
            onSave={handleSave}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            userCount={userCountByRole[role.id] || 0}
          />
        ))}
      </Stack>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{duplicatePerms ? 'Duplicate Role' : 'Create New Role'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Role Name"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
          {duplicatePerms && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Permissions will be copied from the original role.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newRoleName.trim() || creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Users Tab ──────────────────────────────────────────────────────────

const UsersTab: React.FC<{
  users: OrgChartUser[];
  roles: TenantRole[];
  onReload: () => void;
  isAdmin: boolean;
}> = ({ users, roles, onReload, isAdmin }) => {
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const handleRoleChange = async (userId: string, roleId: string | null) => {
    setSaving(s => ({ ...s, [userId]: true }));
    try {
      await usersApi.setUserRole(userId, roleId);
      onReload();
    } catch {
      // ignore
    } finally {
      setSaving(s => ({ ...s, [userId]: false }));
    }
  };

  const handleManagerChange = async (userId: string, managerId: string | null) => {
    setSaving(s => ({ ...s, [userId]: true }));
    try {
      await usersApi.setManager(userId, managerId);
      onReload();
    } catch {
      // ignore
    } finally {
      setSaving(s => ({ ...s, [userId]: false }));
    }
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${tokens.radius.lg}px` }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Manager</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const role = roles.find(r => r.id === user.tenantRoleId);
            const perms = role?.permissions;
            const pageCount = perms?.pages ? Object.values(perms.pages).filter(Boolean).length : 0;
            const editCount = perms?.canEdit ? Object.values(perms.canEdit).filter(Boolean).length : 0;

            return (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user.displayName || user.username || '—'}
                    {user.isOrgAdmin && (
                      <Chip label="Org Admin" size="small" color="primary" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={users.filter(u => u.id !== user.id)}
                    getOptionLabel={(u) => u.displayName || u.username || u.email}
                    value={users.find(u => u.id === user.managerId) || null}
                    onChange={(_, val) => handleManagerChange(user.id, val?.id || null)}
                    renderInput={(params) => <TextField {...params} variant="standard" placeholder="None" />}
                    sx={{ minWidth: 160 }}
                    disabled={!isAdmin || user.isOrgAdmin || saving[user.id]}
                  />
                </TableCell>
                <TableCell>
                  {user.isOrgAdmin ? (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">Full access</Typography>
                  ) : (
                    <Autocomplete
                      size="small"
                      options={roles}
                      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
                      value={role || null}
                      onChange={(_, val) => {
                        const r = val as TenantRole | null;
                        handleRoleChange(user.id, r?.id || null);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant="standard" placeholder="No role assigned" />
                      )}
                      sx={{ minWidth: 160 }}
                      disabled={!isAdmin || saving[user.id]}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.isOrgAdmin ? (
                    <Typography variant="body2" color="text.secondary">All pages</Typography>
                  ) : role ? (
                    <Tooltip title={
                      <Box>
                        <Typography variant="caption" fontWeight={600}>Can View:</Typography>
                        <Typography variant="caption" display="block">
                          {ALL_PAGE_KEYS.filter(k => perms?.pages?.[k]).map(k => PAGE_LABELS[k]).join(', ') || 'None'}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>Can Edit:</Typography>
                        <Typography variant="caption" display="block">
                          {ALL_PAGE_KEYS.filter(k => perms?.canEdit?.[k]).map(k => PAGE_LABELS[k]).join(', ') || 'None'}
                        </Typography>
                      </Box>
                    }>
                      <Typography variant="body2" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                        {pageCount} view, {editCount} edit
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="error.main">No role</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────

const OrgAdminPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const isAdmin = !!dbUser?.isOrgAdmin;
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<OrgChartUser[]>([]);
  const [tenantRoles, setTenantRoles] = useState<TenantRole[]>([]);

  const loadAll = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usersApi.getOrgChart({ scope: 'managed' }),
        usersApi.getRoles(),
      ]);
      setUsers(usersRes.data);
      setTenantRoles(rolesRes.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Management</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage roles and assign them to users. Roles control what pages users can view and edit.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Roles (${tenantRoles.length})`} />
        <Tab label={`Users (${users.length})`} />
      </Tabs>

      {tab === 0 && (
        <RolesTab roles={tenantRoles} users={users} onReload={loadAll} />
      )}

      {tab === 1 && (
        <UsersTab users={users} roles={tenantRoles} onReload={loadAll} isAdmin={isAdmin} />
      )}
    </Box>
  );
};

export default OrgAdminPage;
